import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { SecuritiesService as WizeSecuritiesService } from '@wize-mobile-api';
import { InstrumentMapper } from './instrument.mapper';
import { Instrument, PricePeriod, PriceSeries, WatchlistItem } from '@qvs/core';
import { loadInstrumentList, loadWatchlist } from '~/modules/instrument/slice/instrument.actions';
import { environment } from '#environment';

@Injectable({
  providedIn: 'root'
})
export class InstrumentService {
  private readonly onlyLoadTradeableSecurities = true;
  private readonly lastPriceIncluded = true;

  constructor(
    private readonly store: Store,
    private readonly wizeSecuritiesService: WizeSecuritiesService
  ) {}

  public loadInstruments = (): Observable<Instrument[]> =>
    this.wizeSecuritiesService
      .getSecurities(this.onlyLoadTradeableSecurities, this.lastPriceIncluded)
      .pipe(switchMap((wizeSecurities) => of(InstrumentMapper.fromWizeSecurities(wizeSecurities))));

  public loadInstrumentDetails = (id: number): Observable<Instrument> =>
    this.wizeSecuritiesService
      .getSecurity(id)
      .pipe(switchMap((wizeSecurity) => of(InstrumentMapper.fromWizeSecurityDetails(wizeSecurity))));

  public loadInstrumentPrices = (id: number, period: Uppercase<PricePeriod>): Observable<PriceSeries> =>
    this.wizeSecuritiesService
      .getSecurityPrices(id, period)
      .pipe(switchMap((prices) => of(InstrumentMapper.fromWizeSecurityPrices(prices))));

  public loadWatchlist = (): Observable<Instrument[]> =>
    this.wizeSecuritiesService
      .getSecurityFavorites(this.lastPriceIncluded)
      .pipe(switchMap((wizeSecurities) => of(InstrumentMapper.fromWizeSecurities(wizeSecurities))));

  public addToWatchlist = (id: number): Observable<WatchlistItem> =>
    this.wizeSecuritiesService
      .addSecurityFavorite(id)
      .pipe(switchMap((response) => of(InstrumentMapper.fromWizeSecurityFavoriteMobileCreationResponse(response))));

  public removeFromWatchlist = (id: number): Observable<unknown> => this.wizeSecuritiesService.deleteFavorite(id);

  public init(): Promise<void> {
    if (environment.featureFlags.isInvestmentsEnabled) {
      this.store.dispatch(loadInstrumentList());
      this.store.dispatch(loadWatchlist());
    }
    return Promise.resolve();
  }
}
